import { useLayoutEffect, useState, type RefObject } from "react";

/**
 * An element's width, followed as it changes: what a view inside a window decides
 * its layout from, since the viewport says nothing about how much room it has.
 * Null until measured.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    // 0 is a box not laid out — a hidden tab (content-visibility) — never a real width to lay out for.
    const first = element.getBoundingClientRect().width;
    if (first > 0) setWidth(first);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect.width;
      if (next) setWidth(Math.round(next));
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [ref]);
  return width;
}
